import React, { useState, useEffect, useMemo } from 'react';
import './css/DataTable.css';

const DataTable = ({
  data = [],
  columns: columnsProp = [],
  loading = false,
  error = null,
  actions = null,
  headerActions = null,
  storageKey = null,
  defaultHiddenCols = [],
  emptyText = 'Keine Einträge vorhanden.',
  searchPlaceholder = 'Suchen...',
  initialPageSize = 10,
  pageSizeOptions = [10, 25, 50],
  tabs = null,
  activeTab = null,
  onTabChange = null,
  expandable = null,
  cardHeader = null,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterKey, setFilterKey] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [showColPanel, setShowColPanel] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleExpand = (i) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };
  const [hiddenCols, setHiddenCols] = useState(() => {
    if (!storageKey) return defaultHiddenCols;
    try {
      const s = localStorage.getItem(storageKey);
      return s ? JSON.parse(s) : defaultHiddenCols;
    } catch { return defaultHiddenCols; }
  });

  const columns = useMemo(() => {
    if (columnsProp.length > 0) return columnsProp;
    if (data.length === 0) return [];
    return Object.keys(data[0]).map(k => ({ key: k, label: k }));
  }, [columnsProp, data]);

  const visibleCols = useMemo(
    () => columns.filter(col => !hiddenCols.includes(col.label)),
    [columns, hiddenCols]
  );

  const toggleCol = (label) => {
    const next = hiddenCols.includes(label)
      ? hiddenCols.filter(l => l !== label)
      : [...hiddenCols, label];
    setHiddenCols(next);
    if (storageKey) localStorage.setItem(storageKey, JSON.stringify(next));
  };

  const getRawValue = (row, col) => {
    if (!col.key) return '';
    const raw = row[col.key];
    if (raw == null) return '';
    if (typeof raw === 'object' && 'value' in raw) {
      const v = raw.value;
      if (v == null) return '';
      if (Array.isArray(v)) return v.join(', ');
      return String(v);
    }
    if (typeof raw === 'object') return '';
    return String(raw);
  };

  const getSearchValue = (row, col) => {
    if (col.searchValue) return col.searchValue(row[col.key], row);
    return getRawValue(row, col);
  };

  const filtered = useMemo(() => {
    let result = data;
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      result = result.filter(row =>
        columns.some(col => getSearchValue(row, col).toLowerCase().includes(t))
      );
    }
    if (filterKey && filterValue) {
      const col = columns.find(c => c.label === filterKey);
      if (col) {
        const t = filterValue.toLowerCase();
        result = result.filter(row => getSearchValue(row, col).toLowerCase().includes(t));
      }
    }
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchTerm, filterKey, filterValue, columns]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const col = columns.find(c => c.label === sortKey);
    if (!col) return filtered;
    return [...filtered].sort((a, b) => {
      const av = getSearchValue(a, col);
      const bv = getSearchValue(b, col);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sortKey, sortDir, columns]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const page = Math.min(currentPage, totalPages);
  const paginated = sorted.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, filterKey, filterValue, data]);

  const handleSort = (label) => {
    if (sortKey === label) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(label); setSortDir('asc'); }
  };

  const renderCell = (row, col) => {
    const val = col.key ? row[col.key] : undefined;
    if (col.render) return col.render(val, row);
    if (val == null) return <span className="dt-muted">—</span>;
    if (val === true || val === 1) return <span className="dt-badge dt-badge-yes">Ja</span>;
    if (val === false || val === 0) return <span className="dt-badge dt-badge-no">Nein</span>;
    if (typeof val === 'object' && 'value' in val) {
      const v = val.value;
      if (v === true || v === 1) return <span className="dt-badge dt-badge-yes">Ja</span>;
      if (v === false || v === 0) return <span className="dt-badge dt-badge-no">Nein</span>;
      if (Array.isArray(v)) return v.length ? v.join(', ') : <span className="dt-muted">—</span>;
      return v ?? <span className="dt-muted">—</span>;
    }
    if (typeof val === 'object') return <span className="dt-muted">—</span>;
    return String(val);
  };

  const pageButtons = () => {
    const btns = [];
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);
    for (let i = start; i <= end; i++) {
      btns.push(
        <button
          key={i}
          className={`dt-page-btn${i === page ? ' dt-page-btn-active' : ''}`}
          onClick={() => setCurrentPage(i)}
        >{i}</button>
      );
    }
    return btns;
  };

  if (loading) {
    return (
      <div className="dt-loading">
        <div className="dt-spinner" />
        <span>Wird geladen…</span>
      </div>
    );
  }

  if (error) {
    return <div className="dt-error">Fehler beim Laden: {error}</div>;
  }

  return (
    <div className="dt-container">
      {/* Tabs */}
      {tabs && (
        <div className="dt-tabs">
          {tabs.map(tab => (
            <button
              key={tab.key}
              className={`dt-tab${activeTab === tab.key ? ' dt-tab-active' : ''}`}
              onClick={() => onTabChange && onTabChange(tab.key)}
            >
              {tab.label}
              {tab.count != null && (
                <span className={`dt-tab-count${activeTab === tab.key ? ' dt-tab-count-active' : ''}`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="dt-toolbar">
        <div className="dt-search-wrap">
          <svg className="dt-search-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="8.5" cy="8.5" r="5.5" /><path d="M15 15l3 3" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            className="dt-search"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="dt-clear-btn" onClick={() => setSearchTerm('')} aria-label="Suche löschen">×</button>
          )}
        </div>

        <div className="dt-filter-wrap">
          <select className="dt-select" value={filterKey} onChange={e => { setFilterKey(e.target.value); setFilterValue(''); }}>
            <option value="">Filter: Alle Spalten</option>
            {columns.map(col => (
              <option key={col.label} value={col.label}>{col.label}</option>
            ))}
          </select>
          {filterKey && (
            <input
              type="text"
              className="dt-input"
              placeholder="Wert eingeben…"
              value={filterValue}
              onChange={e => setFilterValue(e.target.value)}
            />
          )}
        </div>

        <div className="dt-toolbar-right">
          {storageKey && (
            <button className={`dt-icon-btn${showColPanel ? ' dt-icon-btn-active' : ''}`} onClick={() => setShowColPanel(p => !p)} title="Spalten anpassen">
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                <rect x="2" y="2" width="4" height="16" rx="1" /><rect x="8" y="2" width="4" height="16" rx="1" /><rect x="14" y="2" width="4" height="16" rx="1" />
              </svg>
              <span>Spalten</span>
            </button>
          )}
          {headerActions}
        </div>
      </div>

      {/* Column panel */}
      {showColPanel && storageKey && (
        <div className="dt-col-panel">
          <span className="dt-col-panel-title">Spalten anzeigen:</span>
          {columns.map(col => (
            <label key={col.label} className="dt-col-check">
              <input
                type="checkbox"
                checked={!hiddenCols.includes(col.label)}
                onChange={() => toggleCol(col.label)}
              />
              {col.label}
            </label>
          ))}
        </div>
      )}

      {/* Stats bar */}
      <div className="dt-stats-bar">
        <span className="dt-stats-count">
          {sorted.length} {sorted.length !== data.length ? `von ${data.length} ` : ''}
          {sorted.length === 1 ? 'Eintrag' : 'Einträge'}
        </span>
        <label className="dt-page-size-label">
          <span>Einträge/Seite:</span>
          <select className="dt-select-sm" value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>
            {pageSizeOptions.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </div>

      {/* Desktop Table */}
      <div className="dt-table-wrap">
        <table className="dt-table">
          <thead>
            <tr>
              {(actions || expandable) && <th className="dt-th dt-th-actions">Aktionen</th>}
              {visibleCols.map(col => (
                <th
                  key={col.label}
                  className={`dt-th${col.sortable !== false ? ' dt-th-sortable' : ''}${sortKey === col.label ? ' dt-th-sorted' : ''}`}
                  onClick={() => col.sortable !== false && handleSort(col.label)}
                >
                  <span className="dt-th-content">
                    {col.label}
                    {col.sortable !== false && (
                      <span className="dt-sort-icon">
                        {sortKey === col.label ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                      </span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={visibleCols.length + ((actions || expandable) ? 1 : 0)} className="dt-empty-cell">
                  <div className="dt-empty">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="40" height="40" className="dt-empty-icon">
                      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span>{emptyText}</span>
                  </div>
                </td>
              </tr>
            ) : (
              paginated.map((row, i) => (
                <React.Fragment key={i}>
                  <tr className="dt-row">
                    {(actions || expandable) && (
                      <td className="dt-td dt-td-actions">
                        {expandable && (
                          <button
                            className={`dt-expand-btn${expandedRows.has(i) ? ' dt-expand-btn-open' : ''}`}
                            onClick={() => toggleExpand(i)}
                            title={expandedRows.has(i) ? 'Einklappen' : 'Details anzeigen'}
                          >
                            {expandedRows.has(i) ? '▲' : '▼'}
                          </button>
                        )}
                        {actions && actions(row)}
                      </td>
                    )}
                    {visibleCols.map(col => (
                      <td key={col.label} className="dt-td">{renderCell(row, col)}</td>
                    ))}
                  </tr>
                  {expandable && expandedRows.has(i) && (
                    <tr className="dt-expanded-row">
                      <td colSpan={visibleCols.length + ((actions || expandable) ? 1 : 0)} className="dt-expanded-cell">
                        {expandable(row, i)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="dt-cards">
        {paginated.length === 0 ? (
          <div className="dt-empty dt-empty-cards">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="40" height="40" className="dt-empty-icon">
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>{emptyText}</span>
          </div>
        ) : (
          paginated.map((row, i) => {
            const usesCustomHeader = typeof cardHeader === 'function';
            const [headerCol, ...bodyCols] = visibleCols;
            const cardBodyCols = usesCustomHeader ? visibleCols : bodyCols;
            return (
              <div key={i} className="dt-card">
                {usesCustomHeader ? (
                  <div className="dt-card-header">
                    <div className="dt-card-header-value">{cardHeader(row)}</div>
                  </div>
                ) : headerCol && (
                  <div className="dt-card-header">
                    <span className="dt-card-header-label">{headerCol.label}</span>
                    <div className="dt-card-header-value">{renderCell(row, headerCol)}</div>
                  </div>
                )}
                {cardBodyCols.map(col => (
                  <div key={col.label} className="dt-card-row">
                    <span className="dt-card-label">{col.label}</span>
                    <span className="dt-card-value">{renderCell(row, col)}</span>
                  </div>
                ))}
                {(actions || expandable) && (
                  <div className="dt-card-actions">
                    {actions && actions(row)}
                    {expandable && (
                      <button
                        className={`dt-btn dt-btn-outline-secondary${expandedRows.has(i) ? ' active' : ''}`}
                        onClick={() => toggleExpand(i)}
                      >
                        {expandedRows.has(i) ? '▲ Einklappen' : '▼ Details anzeigen'}
                      </button>
                    )}
                  </div>
                )}
                {expandable && expandedRows.has(i) && (
                  <div className="dt-card-expand-content">
                    {expandable(row, i)}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="dt-pagination">
          <button className="dt-page-btn" onClick={() => setCurrentPage(1)} disabled={page === 1} title="Erste Seite">«</button>
          <button className="dt-page-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={page === 1} title="Vorherige Seite">‹</button>
          {pageButtons()}
          <button className="dt-page-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} title="Nächste Seite">›</button>
          <button className="dt-page-btn" onClick={() => setCurrentPage(totalPages)} disabled={page === totalPages} title="Letzte Seite">»</button>
          <span className="dt-page-info">Seite {page} / {totalPages}</span>
        </div>
      )}
    </div>
  );
};

export default DataTable;
